import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Key, Plus, Copy, Check, Code, BookOpen, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { businessApi } from '../utils/api';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import CryptoTicker from '../components/CryptoTicker';
import Footer from '../components/Footer';
import { FadeUp, StaggerContainer, StaggerItem } from '../components/Motion';

const Business = () => {
  const { user } = useAuth();
  const [keys, setKeys] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [copiedKey, setCopiedKey] = useState('');

  useEffect(() => { if (user) fetchKeys(); }, [user]);

  const fetchKeys = async () => {
    try {
      const response = await businessApi.getKeys();
      setKeys(response.data);
    } catch (error) { console.error('Error fetching keys:', error); }
  };

  const createKey = async () => {
    if (!keyName.trim()) { toast.error('Veuillez entrer un nom pour la clé'); return; }
    try {
      await businessApi.createKey({ name: keyName, permissions: ['read', 'trade'] });
      toast.success('Clé API créée avec succès !');
      setKeyName('');
      setShowCreateModal(false);
      fetchKeys();
    } catch { toast.error('Erreur lors de la création de la clé'); }
  };

  const copyToClipboard = (key) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    toast.success('Clé copiée !');
    setTimeout(() => setCopiedKey(''), 2000);
  };

  return (
    <div className="min-h-screen bg-white">
      <CryptoTicker />
      <Header />

      {/* Black hero */}
      <section className="bg-black pt-20 pb-24 px-6" data-testid="business-hero">
        <div className="max-w-5xl mx-auto text-center">
          <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-white font-extrabold text-5xl md:text-7xl tracking-[-0.03em] leading-[1.02] mb-6" style={{ fontFamily: 'Manrope' }}
            data-testid="business-title">
            API Business
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}
            className="text-white/60 text-lg max-w-2xl mx-auto mb-10" data-testid="business-subtitle">
            Intégrez alphaswapp dans vos applications. Taux en temps réel, échanges automatisés et clés API sécurisées.
          </motion.p>
          {!user && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
              <Link to="/auth"><button className="btn-pill-white px-8 py-4 text-base inline-flex items-center space-x-2"><span>Obtenir une clé API</span><ArrowRight className="w-5 h-5" /></button></Link>
            </motion.div>
          )}
        </div>
      </section>

      <div className="px-6 pb-16 -mt-10" data-testid="business-container">
        <div className="max-w-6xl mx-auto">
          {/* Feature cards */}
          <StaggerContainer className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16">
            {[
              { icon: Code, title: 'REST API', desc: "API RESTful complète pour tous vos besoins d'échange", cls: 'gradient-blue text-white', sub: 'text-white/70', testid: 'feature-rest-api' },
              { icon: Key, title: 'Authentification', desc: 'Clés API sécurisées avec permissions granulaires', cls: 'gradient-pink-violet text-white', sub: 'text-white/70', testid: 'feature-realtime' },
              { icon: BookOpen, title: 'Documentation', desc: 'Guides complets et exemples de code', cls: 'gradient-metal text-white', sub: 'text-white/60', testid: 'feature-docs' },
            ].map((f, idx) => {
              const Icon = f.icon;
              return (
                <StaggerItem key={idx} className={`rounded-3xl p-8 ${f.cls}`} data-testid={f.testid}>
                  <div className="w-12 h-12 rounded-full bg-black/10 flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6" strokeWidth={1.8} />
                  </div>
                  <h3 className="text-2xl font-extrabold mb-2 tracking-tight" style={{ fontFamily: 'Manrope' }}>{f.title}</h3>
                  <p className={`text-sm ${f.sub}`}>{f.desc}</p>
                </StaggerItem>
              );
            })}
          </StaggerContainer>

          {user && (
            <FadeUp className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.05)] mb-10" data-testid="api-keys-section">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>Vos clés API</h2>
                <button onClick={() => setShowCreateModal(true)}
                  className="btn-pill-dark px-6 py-3 flex items-center space-x-2 text-sm" data-testid="create-key-btn">
                  <Plus className="w-4 h-4" /><span>Nouvelle clé</span>
                </button>
              </div>

              {keys.length === 0 ? (
                <div className="text-center py-12 text-gray-300" data-testid="no-keys-message">
                  <Key className="w-16 h-16 mx-auto mb-4 opacity-40" />
                  <p className="text-gray-400">Aucune clé API créée. Cliquez sur "Nouvelle clé" pour commencer.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {keys.map((key, idx) => (
                    <div key={key.id} className="bg-[#F5F6F7] rounded-2xl p-6" data-testid={`api-key-${idx}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="font-bold mb-2 text-gray-900">{key.name}</div>
                          <div className="font-mono-data text-sm text-gray-600 bg-white px-3 py-2 rounded-xl inline-block border border-gray-200 truncate max-w-full">
                            {key.key}
                          </div>
                        </div>
                        <button onClick={() => copyToClipboard(key.key)}
                          className="ml-4 bg-white p-3 rounded-full hover:bg-gray-100 transition-colors border border-gray-200"
                          data-testid={`copy-key-btn-${idx}`}>
                          {copiedKey === key.key ? <Check className="w-5 h-5 text-emerald-600" /> : <Copy className="w-5 h-5 text-gray-500" />}
                        </button>
                      </div>
                      <div className="mt-4 flex items-center space-x-2 text-sm text-gray-500">
                        <span>Permissions :</span>
                        {key.permissions.map((perm, i) => (
                          <span key={i} className="bg-gray-900 text-white px-2.5 py-0.5 rounded-full text-xs font-semibold">{perm}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </FadeUp>
          )}

          {/* Documentation — dark Revolut block */}
          <FadeUp className="bg-black rounded-3xl p-8 md:p-10" data-testid="api-docs">
            <h2 className="text-3xl font-extrabold mb-8 text-white tracking-tight" style={{ fontFamily: 'Manrope' }}>Documentation API</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-bold mb-4 text-white/80">Endpoints disponibles</h3>
                <div className="space-y-3">
                  {[
                    { method: 'GET', color: 'text-emerald-400', path: '/api/currencies', desc: 'Liste des devises et cryptomonnaies disponibles' },
                    { method: 'GET', color: 'text-emerald-400', path: '/api/exchange/rates', desc: 'Taux de change en temps réel' },
                    { method: 'POST', color: 'text-sky-400', path: '/api/exchange/lock-rate', desc: 'Verrouiller un taux pendant 10 minutes' },
                    { method: 'POST', color: 'text-sky-400', path: '/api/exchange/request', desc: "Créer une demande d'échange" },
                  ].map((e, i) => (
                    <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-4">
                      <div className="font-mono-data text-sm mb-1 text-white"><span className={`${e.color} font-bold`}>{e.method}</span> {e.path}</div>
                      <div className="text-white/40 text-sm">{e.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-4 text-white/80">Exemple d'utilisation</h3>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <p className="text-white/50 mb-4 text-sm">Incluez votre clé dans l'en-tête Authorization :</p>
                  <div className="bg-black rounded-xl p-4 font-mono-data text-sm text-emerald-400 overflow-x-auto border border-white/10">
                    Authorization: Bearer YOUR_API_KEY
                  </div>
                  <p className="text-white/30 text-sm mt-4">Remplacez YOUR_API_KEY par votre clé API générée ci-dessus.</p>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowCreateModal(false)} data-testid="create-key-modal">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl p-8 max-w-md w-full mx-4 shadow-[0_24px_80px_rgb(0,0,0,0.3)]" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-2xl font-extrabold mb-6 text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>Créer une nouvelle clé API</h3>
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2 text-gray-600">Nom de la clé</label>
              <input type="text" value={keyName} onChange={(e) => setKeyName(e.target.value)}
                placeholder="Ex: Mon Application"
                className="w-full bg-[#F5F6F7] rounded-2xl px-5 py-3.5 text-gray-900 outline-none border border-transparent placeholder-gray-400"
                data-testid="key-name-input" />
            </div>
            <div className="flex space-x-3">
              <button onClick={createKey} className="flex-1 btn-pill-dark py-3.5" data-testid="confirm-create-key-btn">Créer</button>
              <button onClick={() => setShowCreateModal(false)} className="flex-1 btn-pill-ghost py-3.5" data-testid="cancel-create-key-btn">Annuler</button>
            </div>
          </motion.div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Business;
